import React, { Component } from 'react';
import { QueryRenderer } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';
import { Grid,  Paper, Table, TableHead, TableCell, TableBody, TableRow, TextField, Typography } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { format, addMinutes } from 'date-fns';

import PageTitle from '../ui/PageTitle';

import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import { green } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();
const PayoutDetailInstantQuery = graphql`
query PayoutDetailInstantQuery($vendorId: UUID!, $resellerId: UUID!, $start: Datetime, $end: Datetime) {
  getinstantpayoutlines(vendoraccountid: $vendorId, reselleraccountid: $resellerId, startts: $start, endts: $end) {
     nodes {
      vendorPayoutId
      resellerAccountId
      resellerAccountName
      customerAccountName
      customerAccountId
      programName
      programType
      appName
      status
      calculationAudit
      autoPayout
      chargeAmountTotal
      payoutAmount
      autoPayout
      approvedTime
      approvedBy
      chargeAmountRangeTotal
      payoutAmountRangeTotal
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
    justifyContent: 'flex-end',
    backgroundColor: theme.palette.background.default,
  },
  flex: {
    flex: 1,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'left',
    color: theme.palette.text.secondary,
    maxWidth: 1200,
    marginTop: 16,
  },
  detailIcon: {
    width: 22,
    height: 22,
  }
});

class PayoutDetailInstant extends Component {

  constructor(props) {
    super(props);

    this.state = {
      reFetch: 0  
    };  
  }


  render() {
    const { isAuthenticated, isAuthenticatedVendor, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;
    const okColor = green[300];
    
    const params = new URLSearchParams(this.props.location.search);
    let vendorId = abEnvironment.getUserAttribute("a_id");
    let resellerId = params.get('resellerId');
    let startDate = params.get('start');
    let endDate = params.get('end');

    if (!isAuthenticated() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    } else if (isAuthenticatedVendor()) {
      return (
        <Grid container spacing={3} direction="row" justifyContent="flex-start">
          <Grid item xs={12}>
            <PageTitle title="Payout details" subtitle="View instant payout details." showBackButton={true} name="Payouts" noMargin={true} history={this.props.history}/>
          </Grid>
          <Grid item xs={12}>
            <QueryRenderer
              environment={abEnvironment.getEnvironment()}
              query={PayoutDetailInstantQuery}
              variables={{vendorId: vendorId, resellerId: resellerId, start: startDate, end: endDate, forceFetch: this.state.reFetch}}
              render={({error, props})=> {
                if (error) {
                  logError(error.message);
                  return (
                    <ServerError/>
                  );
                } else if (props) {

                  let detailCharges = props.getinstantpayoutlines.nodes;

                  let totalCharges = Number(detailCharges[0].chargeAmountRangeTotal).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                  let totalEarned = Number(detailCharges[0].payoutAmountRangeTotal).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                  
                  let rowCount = 0;

                  let fStartDate = format(new Date(startDate), "yyyy-MM-dd");
                  let fEndDate = format(new Date(endDate), "yyyy-MM-dd");

                  return ( 
                    <div  style={{marginTop:24}}>
                      <Paper className={classes.paper}>
                        <Grid container spacing={2} direction="row" justifyContent="space-between" style={{marginBottom: '10px'}}>
                          <Grid item xs={12}>
                            <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                              {'Payout transactions for ' + detailCharges[0].resellerAccountName + ' from ' + fStartDate + ' through ' + fEndDate}
                            </Typography>
                          </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" style={AppBindStyles.sectionTitleTable}>
                              Totals for period
                            </Typography>
                          </Grid>
  
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="total-charges" label={'Total Charges'}
                                value={ totalCharges }
                                InputProps={{ readOnly: true, disableUnderline: true }}                              
                                />
                            </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                                id="total-earned" label={'Total Earned'}
                                value={ totalEarned }
                                InputProps={{ readOnly: true, disableUnderline: true }} 
                                />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="h6" style={AppBindStyles.sectionTitleTable}>
                              Details
                            </Typography>
                          </Grid>

                          <Grid>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Transaction time</TableCell>
                                  <TableCell>Program name</TableCell>
                                  <TableCell>Customer name</TableCell>
                                  <TableCell>App name</TableCell>
                                  <TableCell align="right">Charge amount</TableCell>
                                  <TableCell align="right">Earned amount</TableCell>
                                  <TableCell>Approved</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>

                                {detailCharges.map(n => {

                                  let chargeAmount = Number(n.chargeAmountTotal).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                                  let earnedAmount = Number(n.payoutAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                                  let aDate = new Date(n.approvedTime);
                                  //let transactionTime = format(aDate, 'yyyy-MM-dd HH:mm:ss');
                                  let transactionTimeLocal = format(addMinutes(aDate, aDate.getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss');

                                  return (
                                    <TableRow key={rowCount++}>
                                      <TableCell>{transactionTimeLocal}</TableCell>
                                      <TableCell>{n.programName}</TableCell>
                                      <TableCell>{n.customerAccountName}</TableCell>
                                      <TableCell>{n.appName}</TableCell>
                                      <TableCell align="right">{chargeAmount}</TableCell>
                                      <TableCell align="right">{earnedAmount}</TableCell>
                                      <TableCell align="center"><CheckCircle htmlColor={okColor}/></TableCell>
                                    </TableRow>
                                  );
                                })}
                                </TableBody>
                            </Table>
                          </Grid>

                        </Grid>

                      </Paper>
  
                    </div>
                  )
                }
                return (
                  <div>
                    <Typography>Loading...</Typography>
                    <LoadingSpinner/>
                  </div>
                );
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Redirect to='/home' />
      );
    }
  }

}

PayoutDetailInstant.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(PayoutDetailInstant);
